
import React, { useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, X, Send, Loader2, AlertCircle, RefreshCcw, Zap } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { useSupportChat } from '@/hooks/useSupportChat';
import { SupportMessage } from '@/components/support/SupportMessage';
import { useAuth } from '@/context/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

export const SupportChat: React.FC = () => {
  const {
    isOpen,
    toggleChat,
    messages,
    newMessage,
    setNewMessage,
    sendMessage,
    isLoading,
    isTyping,
    connectionError,
    retryLastUserMessage,
    isRetrying,
    quickActions,
    showQuickActions,
    handleQuickAction
  } = useSupportChat();
  const { isAuthenticated } = useAuth();
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  // Support chat is now available for all users (authenticated and anonymous)
  return (
    <div className="fixed bottom-24 right-8 z-50">
      {!isOpen ? (
        <Button
          onClick={toggleChat}
          className="rounded-full w-14 h-14 bg-brand-500 hover:bg-brand-600 shadow-lg flex items-center justify-center"
        >
          <MessageCircle className="text-white" size={24} />
        </Button>
      ) : (
        <Card className="w-[370px] h-[550px] shadow-xl flex flex-col mb-2">
          <CardHeader className="bg-brand-500 text-white py-3 px-4 flex flex-row justify-between items-center">
            <div>
              <h3 className="font-semibold text-md">Mbështetja TreguIm</h3>
              <p className="text-xs text-brand-100">
                {isAuthenticated ? 'Ndihmë e specializuar për platformën' : 'Ndihmë për vizitorët - Regjistrohu për më shumë'}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleChat} className="text-white hover:bg-brand-600">
              <X size={18} />
            </Button>
          </CardHeader>
          <CardContent className="flex-grow overflow-y-auto p-3">
            {connectionError && (
              <Alert variant="destructive" className="mb-3">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  {connectionError}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={retryLastUserMessage} 
                    className="ml-2 h-6 px-2 text-xs"
                    disabled={isLoading || isRetrying}
                  >
                    <RefreshCcw className="mr-1 h-3 w-3" /> Provo përsëri
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            
            {messages.length === 0 && !connectionError && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  {isLoading ? (
                    <LoadingSpinner size="sm" className="mx-auto mb-2" />
                  ) : (
                    <MessageCircle className="mx-auto mb-2 text-gray-400" size={24} />
                  )}
                  <p className="text-sm">Duke u lidhur me mbështetjen TreguIm...</p>
                  {!isAuthenticated && (
                    <p className="text-xs mt-2 text-blue-600">
                      💡 Tip: Regjistrohu për të ruajtur historinë e bisedës
                    </p>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {messages.map((message) => (
                <SupportMessage key={message.id} message={message} />
              ))}
              
              {/* Quick Actions */}
              {showQuickActions && !isLoading && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="text-blue-500" size={16} />
                    <span className="text-sm font-medium text-blue-700">Pyetje të shpeshta</span>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {quickActions.slice(0, 4).map((action) => (
                      <Button
                        key={action.id}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAction(action)}
                        className="text-left justify-start text-xs h-8 bg-white border-blue-200 hover:bg-blue-50"
                      >
                        {action.text}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {isTyping && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <MessageCircle size={16} />
                  </div>
                  <div className="bg-gray-100 rounded-lg px-4 py-2 max-w-[75%]">
                    <div className="animate-pulse flex gap-1">
                      <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </CardContent>
          <CardFooter className="p-3 border-t">
            <form onSubmit={handleSubmit} className="flex w-full gap-2">
              <Input
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Shkruani pyetjen tuaj..."
                className="flex-1"
                disabled={isLoading || isRetrying}
              />
              <Button type="submit" size="icon" disabled={!newMessage.trim() || isLoading || isRetrying}>
                {isLoading || isRetrying ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              </Button>
            </form>
          </CardFooter>
        </Card>
      )}
    </div>
  );
};
